import React, { useState, useEffect, useRef } from 'react';
import { Tradesperson } from '@/lib/types';
import { getTradeDisplayName, getTradeIconForTrade, getTradeType } from '@/lib/tradeUtils';
import { Phone, MessageSquare, MoreVertical, Check, Copy, Mail, Share2, BookmarkPlus, Trash2, Edit, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { useAuth } from '@/contexts/AuthContext';
import { addUserTradesperson, isTradesPersonSavedByUser, updateUserTradesperson } from '@/lib/supabase/trades';
import { debug } from '@/lib/utils';
import { Textarea } from '@/components/ui/textarea';

// Type definitions
interface TradePersonCardProps {
  trade: Tradesperson;
  mode: 'saved' | 'public'; // Whether this is on the saved page or public results
  username?: string; // Current user's username for sharing
  postcode?: string; // Optional postcode for context in messages
  onSave?: () => void; // Optional callback for when a trade is saved
  onRemove?: (tradeId: string) => void; // Optional callback for removing a trade
  onUndoRemove?: (tradeId: string) => void; // Optional callback for undoing a removal
  notes?: string; // Optional notes from the user-tradesperson relationship
  relationId?: string; // Optional relationship ID for updating notes
}

export function TradePersonCard({
  trade,
  mode,
  username,
  postcode,
  onSave,
  onRemove,
  onUndoRemove,
  notes: initialNotes = '',
  relationId
}: TradePersonCardProps) {
  const { user } = useAuth();
  const [isSaved, setIsSaved] = useState<boolean>(false);
  const [isCheckingSaved, setIsCheckingSaved] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [confirmingDelete, setConfirmingDelete] = useState<boolean>(false);
  const [isRemoved, setIsRemoved] = useState<boolean>(false);
  const [isEditingNotes, setIsEditingNotes] = useState<boolean>(false);
  const [notes, setNotes] = useState<string>(initialNotes || '');
  const [expandedNotes, setExpandedNotes] = useState<boolean>(false);

  const notesRef = useRef<HTMLTextAreaElement>(null);

  const TradeIcon = getTradeIconForTrade(trade);
  const displayName = getTradeDisplayName(trade);
  const tradeType = getTradeType(trade);
  // Get properly capitalized display name for the trade type
  const tradeTypeDisplay = tradeType ? tradeType.charAt(0).toUpperCase() + tradeType.slice(1) : '';

  // Check if this tradesperson is already saved by the user
  useEffect(() => {
    if (!user || !trade.id) return;

    const checkIfSaved = async () => {
      setIsCheckingSaved(true);
      try {
        const saved = await isTradesPersonSavedByUser(user.id, trade.id);
        setIsSaved(saved);
      } catch (error) {
        debug.error('Error checking if tradesperson is saved:', error);
      } finally {
        setIsCheckingSaved(false);
      }
    };

    checkIfSaved();
  }, [user, trade.id]);

  // Focus textarea when editing notes
  useEffect(() => {
    if (isEditingNotes && notesRef.current) {
      notesRef.current.focus();
    }
  }, [isEditingNotes]);

  // Update notes state when prop changes
  useEffect(() => {
    setNotes(initialNotes || '');
  }, [initialNotes]);

  // Handle phone call
  const handleCall = () => {
    if (trade.phone) {
      window.open(`tel:${trade.phone}`);
    } else {
      toast.info("No phone number available");
    }
  };

  // Handle SMS messaging
  const handleMessage = () => {
    if (trade.phone) {
      // Create message text based on context
      let message;
      if (mode === 'saved') {
        // Simpler message for saved trades
        message = `Hi ${displayName}. Do you have availability for some work?`;
      } else {
        // More detailed message for public results
        message = `Hi. I'm in the ${postcode ? postcode.toUpperCase() : 'local'} area and looking for a ${tradeTypeDisplay || 'tradesperson'}. Are you available for work?`;
      }
      window.open(`sms:${trade.phone}?body=${encodeURIComponent(message)}`);
    } else {
      toast.info("No phone number available for SMS");
    }
  };

  // Handle email
  const handleEmail = () => {
    if (trade.email) {
      let subject, body;

      if (mode === 'saved') {
        // Direct message for saved contacts
        subject = encodeURIComponent(`Need your help with some work`);
        body = encodeURIComponent(`Hi ${displayName},\n\nI have some ${tradeTypeDisplay || 'work'} that needs doing. Are you available to help?\n\nPlease let me know your availability.\n\nThanks`);
      } else {
        // More detailed for new contacts
        subject = encodeURIComponent(`Request for ${tradeTypeDisplay || 'work'} in ${postcode ? postcode.toUpperCase() : 'local'} area`);
        body = encodeURIComponent(`Hi,\n\nI'm in the ${postcode ? postcode.toUpperCase() : 'local'} area and I'm looking for a ${tradeTypeDisplay || 'tradesperson'} for some work I need doing.\n\nAre you available for a quote?\n\nThanks,\n[Your name]`);
      }

      window.open(`mailto:${trade.email}?subject=${subject}&body=${body}`);
    } else {
      toast.info("No email address available");
    }
  };

  // Handle copy contact info
  const handleCopyContact = () => {
    const contactText = `${displayName}${trade.phone ? ` - ${trade.phone}` : ''}${trade.email ? ` - ${trade.email}` : ''}`;

    navigator.clipboard.writeText(contactText)
      .then(() => toast.success("Contact info copied to clipboard"))
      .catch(() => toast.error("Failed to copy contact info"));
  };

  // Handle share
  const handleShare = async () => {
    if (!username && mode === 'saved') {
      toast.info("Set up a username in settings to share tradespeople");
      return;
    }

    // Generate the share URL
    const baseUrl = window.location.origin;
    const isTpDomain = window.location.hostname.includes('tradespurple.com');

    // Create URL with path parameter for highlighting specific tradesperson
    const shareUrl = isTpDomain
      ? `${baseUrl}/${username}/tradesperson/${trade.id}`
      : `${baseUrl}/tradespurple/${username}/tradesperson/${trade.id}`;

    // Check if Web Share API is available
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${displayName} - ${tradeTypeDisplay}`,
          text: `Here's a ${tradeTypeDisplay} I recommend: ${displayName}`,
          url: shareUrl,
        });
      } catch (err) {
        // Fallback to clipboard if share fails or is cancelled
        handleCopyShareUrl(shareUrl);
      }
    } else {
      // Fallback for browsers without Web Share API
      handleCopyShareUrl(shareUrl);
    }
  };

  // Helper to copy share URL to clipboard
  const handleCopyShareUrl = (url: string) => {
    navigator.clipboard.writeText(url)
      .then(() => toast.success("Share link copied to clipboard"))
      .catch(() => toast.error("Failed to copy share link"));
  };

  // Handle save tradesperson button click
  const handleSaveClick = async () => {
    if (!user) {
      toast.info("Sign in to save to TradesPurple");
      return;
    }

    if (isSaved) {
      toast.info("Already saved to your TradesPurple");
      return;
    }

    setIsSaving(true);
    try {
      await addUserTradesperson(user.id, trade.id, {
        notes: "",
        preferred: false
      });

      setIsSaved(true);
      toast.success("Added to your TradesPurple");

      // Call onSave callback if provided (for parent component refresh)
      if (onSave) {
        onSave();
      }
    } catch (error) {
      debug.error('Error saving tradesperson:', error);
      toast.error("Failed to add tradesperson");
    } finally {
      setIsSaving(false);
    }
  };

  // Handle saving notes
  const handleSaveNotes = async () => {
    if (!relationId) {
      toast.error("Cannot save notes: missing relationship ID");
      return;
    }

    try {
      await updateUserTradesperson(relationId, { notes });
      toast.success("Notes saved");
      setIsEditingNotes(false);
    } catch (error) {
      debug.error('Error saving notes:', error);
      toast.error("Failed to save notes");
    }
  };

  // Handle cancel notes editing
  const handleCancelNotes = () => {
    setNotes(initialNotes || '');
    setIsEditingNotes(false);
  };

  // If removed, don't render anything
  if (isRemoved) {
    return null;
  }

  // Helper to truncate notes - show more content
  const truncateNotes = (text: string, maxLength = 120) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;

    // Try to break at a natural point (period, line break, etc.)
    const breakPoints = ['. ', '! ', '? ', '\n'];
    for (const point of breakPoints) {
      const pos = text.lastIndexOf(point, maxLength);
      if (pos > maxLength/2) {
        return `${text.substring(0, pos+1).trim()}...`;
      }
    }

    // Fallback to breaking at word boundary
    return `${text.substring(0, maxLength).trim()}...`;
  };

  return (
    <div className={`p-4 border rounded-xl relative shadow-sm ${
      confirmingDelete
        ? 'border-red-200 bg-gray-50/80 opacity-90'
        : 'border-gray-200 bg-white hover:bg-gray-50'
    } transition-colors`}>

      {/* Delete Confirmation Overlay */}
      {confirmingDelete && (
        <div className="absolute inset-0 bg-white/95 rounded-xl flex items-center justify-center z-10 overflow-hidden">
          <div className="px-3 py-4 max-w-full text-center">
            <Trash2 className="mx-auto h-8 w-8 text-red-500 mb-2" />
            <h3 className="text-base font-semibold text-gray-900 mb-1">Remove {displayName || 'tradesperson'}?</h3>
            <p className="text-sm text-gray-600 mb-3">This will remove them from your saved tradespeople list.</p>

            <div className="flex space-x-2 justify-center">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setConfirmingDelete(false)}
                className="px-4 border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg h-9"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => {
                  if (onRemove) {
                    try {
                      // First set the card as removed in the UI for immediate feedback
                      setIsRemoved(true);

                      // Then call the parent component's removal function
                      onRemove(trade.id);

                      // Show success toast with undo option
                      toast.success(`Removed ${displayName || 'tradesperson'} from your list`, {
                        action: {
                          label: "Undo",
                          onClick: () => {
                            if (onUndoRemove) {
                              // If undo is clicked, reset our local state
                              setIsRemoved(false);
                              setConfirmingDelete(false);
                              // And call the parent's undo function
                              onUndoRemove(trade.id);
                            }
                          }
                        }
                      });
                    } catch (error) {
                      debug.error('Error removing tradesperson:', error);
                      toast.error(`Failed to remove ${displayName || 'tradesperson'}`);
                      // Reset states on error
                      setIsRemoved(false);
                      setConfirmingDelete(false);
                    }
                  }
                }}
                className="px-4 bg-red-600 hover:bg-red-700 rounded-lg h-9"
              >
                Remove
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Main Card Content - Compact Design */}
      <div className="flex flex-col">
        {/* Verified Badge (Absolute Positioned) */}
        {trade.verified && (
          <div className="absolute top-3 right-3">
            <Badge className="bg-green-100 text-green-700 border-0 rounded-full px-2 py-0.5 text-xs font-medium">
              <Check className="h-3 w-3 mr-1 stroke-[3]" /> Verified
            </Badge>
          </div>
        )}

        {/* Top Row: Name and Profession */}
        <div className="flex items-start mb-2 pr-20"> {/* Added right padding to make space for the absolute badge */}
          <div className="flex items-start">
            <div className="rounded-xl bg-purple-100 p-2 mr-2.5 flex-shrink-0">
              <TradeIcon className="h-4 w-4 text-purple-700" />
            </div>
            <div>
              <h3 className="font-semibold text-lg text-purple-900 leading-tight">
                {displayName}
              </h3>
              <p className="text-sm text-purple-600 mt-0.5">{tradeTypeDisplay}</p>
            </div>
          </div>
        </div>

        {/* Notes Section - Only shown in saved mode */}
        {mode === 'saved' && (
          <div
            className={`mb-2 px-3 py-2 rounded-md cursor-pointer ${isEditingNotes ? 'bg-gray-50 border border-gray-200' : 'bg-purple-50/40 hover:bg-purple-50/70'}`}
            onClick={() => !isEditingNotes && setIsEditingNotes(true)}
          >
            {isEditingNotes ? (
              <>
                <Textarea
                  ref={notesRef}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="min-h-[80px] text-sm border-gray-300 resize-none"
                  placeholder="Add notes about this tradesperson..."
                />
                <div className="flex justify-end space-x-2 mt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleCancelNotes}
                    className="h-8"
                  >
                    <X className="h-3.5 w-3.5 mr-1" /> Cancel
                  </Button>
                  <Button
                    size="sm"
                    variant="default"
                    onClick={handleSaveNotes}
                    className="h-8 bg-purple-700 hover:bg-purple-800"
                  >
                    <Save className="h-3.5 w-3.5 mr-1" /> Save
                  </Button>
                </div>
              </>
            ) : notes ? (
              <div className="text-sm text-gray-700 min-h-[2.5rem]">
                {expandedNotes ? notes : truncateNotes(notes, 120)}
                {!expandedNotes && notes.length > 120 && (
                  <span className="text-purple-700 ml-1 text-xs font-medium">more</span>
                )}
              </div>
            ) : (
              <div className="text-sm text-gray-400 min-h-[2.5rem] flex items-center">
                <Edit className="h-3.5 w-3.5 mr-1.5 text-gray-400" />
                <span>Add notes...</span>
              </div>
            )}
          </div>
        )}

        {/* Action buttons */}
        <div className="mt-2 flex justify-between">
          {/* Primary action buttons (left side) */}
          <div className="flex space-x-3">
            {trade.phone ? (
              /* Show Phone and SMS buttons if phone number exists */
              <>
                {/* Phone */}
                <Button
                  size="sm"
                  variant="ghost"
                  className="p-2 h-10 w-10 rounded-full bg-purple-50 hover:bg-purple-100 text-purple-700"
                  onClick={handleCall}
                  aria-label="Call"
                >
                  <Phone className="h-4.5 w-4.5" />
                </Button>

                {/* Message */}
                <Button
                  size="sm"
                  variant="ghost"
                  className="p-2 h-10 w-10 rounded-full bg-purple-50 hover:bg-purple-100 text-purple-700"
                  onClick={handleMessage}
                  aria-label="Message"
                >
                  <MessageSquare className="h-4.5 w-4.5" />
                </Button>
              </>
            ) : trade.email ? (
              /* Show Email button if no phone but email exists */
              <Button
                size="sm"
                variant="ghost"
                className="p-2 h-10 w-10 rounded-full bg-purple-50 hover:bg-purple-100 text-purple-700"
                onClick={handleEmail}
                aria-label="Email"
              >
                <Mail className="h-4.5 w-4.5" />
              </Button>
            ) : null}
          </div>

          {/* Secondary actions (right side) */}
          <div className="flex space-x-2 items-center">
            {/* For public mode, show Save button next to More dropdown */}
            {mode === 'public' && (
              <Button
                size="sm"
                variant="outline"
                className={`flex items-center px-4 h-9 rounded-lg
                  ${isSaved
                    ? 'bg-green-100 hover:bg-green-100 text-green-700 border-green-200'
                    : 'bg-white hover:bg-purple-50 text-purple-700 border-purple-300'}`}
                onClick={handleSaveClick}
                disabled={isSaving || isCheckingSaved || isSaved}
                aria-label={isSaved ? "Saved" : "Save"}
              >
                {isSaved ? (
                  <div className="flex items-center">
                    <Check className="h-4 w-4 mr-1.5" />
                    <span className="font-medium text-sm">Saved</span>
                  </div>
                ) : (
                  <div className="flex items-center">
                    <BookmarkPlus className="h-4 w-4 mr-1.5" />
                    <span className="font-medium text-sm">Save</span>
                  </div>
                )}
              </Button>
            )}

            {/* More options dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  className="p-2 h-10 w-10 rounded-full bg-purple-50 hover:bg-purple-100 text-purple-700"
                  aria-label="More options"
                >
                  <MoreVertical className="h-4.5 w-4.5" />
                </Button>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="end" className="w-[180px] rounded-xl overflow-hidden shadow-lg">
                {/* Email in dropdown if available */}
                {/* Only show email in dropdown if phone exists, otherwise it's shown as a primary button */}
                {trade.phone && trade.email && (
                  <DropdownMenuItem onClick={handleEmail} className="py-2.5">
                    <Mail className="mr-2 h-4 w-4" />
                    <span>Send Email</span>
                  </DropdownMenuItem>
                )}

                {/* Share option */}
                {mode === 'saved' && (
                  <DropdownMenuItem onClick={handleShare} className="py-2.5">
                    <Share2 className="mr-2 h-4 w-4" />
                    <span>Share Contact</span>
                  </DropdownMenuItem>
                )}

                <DropdownMenuItem onClick={handleCopyContact} className="py-2.5">
                  <Copy className="mr-2 h-4 w-4" />
                  <span>Copy Contact</span>
                </DropdownMenuItem>

                {mode === 'saved' && (
                  <DropdownMenuItem onClick={() => setIsEditingNotes(true)} className="py-2.5">
                    <Edit className="mr-2 h-4 w-4" />
                    <span>Edit Notes</span>
                  </DropdownMenuItem>
                )}

                {/* Only show remove option in saved mode with onRemove callback provided */}
                {mode === 'saved' && onRemove && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => setConfirmingDelete(true)}
                      className="py-2.5 text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      <span>Remove</span>
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Recommendation count - only shown if available */}
        {trade.recommendation_count !== undefined && trade.recommendation_count > 0 && (
          <div className="flex items-center text-xs text-purple-700 mt-2">
            <Check className="h-3 w-3 mr-1 text-purple-700" />
            <span>
              {trade.recommendation_count} {trade.recommendation_count === 1 ? 'recommendation' : 'recommendations'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
